import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  formatDate,
  getLocaleMonth,
  toast,
} from '@korint/folio';
import { useQuery } from '@tanstack/react-query';
import {
  axeceControllerListDrivequantAverageScoresQueryOptions,
  axeceControllerListDrivequantHistoricalUsersQueryOptions,
  axeceControllerListPossibleDrivequantUsersQueryOptions,
  customerControllerGetCustomerQueryOptions,
  keyValueFieldsToObject,
  useAxeceControllerAttachDrivequantUserIdToCustomer,
} from 'korint-sdk';
import {
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  InfoIcon,
  UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type QuarterMonthRow = { iso: string; email?: string; score?: number };
type AttachWindow = {
  cognitoEmail?: string;
  externalServiceUserId: string;
  attachedAt: string;
  detachedAt: string | null;
};

/** Every calendar month in the repricing period, with the attached driver + score when scored. */
function buildQuarterMonthRows(
  period: { startedAt: string; endedAt: string },
  users: {
    cognitoEmail?: string;
    externalServiceUserId: string;
    monthlyScores: { month: string; score: number }[];
  }[],
  windows: AttachWindow[],
): QuarterMonthRow[] {
  const monthKey = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
  };
  // Driver attached on the last day of the month (same rule the monthly score follows).
  const driverForMonth = (y: number, m: number): string | undefined => {
    const lastDay = Date.UTC(y, m + 1, 0);
    const window = windows.find(({ attachedAt, detachedAt }) => {
      const from = new Date(attachedAt).getTime();
      const to = detachedAt ? new Date(detachedAt).getTime() : Number.POSITIVE_INFINITY;
      return from <= lastDay && lastDay < to;
    });
    return window ? (window.cognitoEmail ?? window.externalServiceUserId) : undefined;
  };
  const rows = new Map<string, QuarterMonthRow>();
  // Enumerate whole months strictly before the end month: the boundary month
  // (period end mid-month) isn't complete yet and belongs to the next period.
  const endDate = new Date(period.endedAt);
  const endMonthStart = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 1);
  const start = new Date(period.startedAt);
  let year = start.getUTCFullYear();
  let month = start.getUTCMonth();
  while (Date.UTC(year, month, 1) < endMonthStart) {
    rows.set(`${year}-${month}`, {
      iso: new Date(Date.UTC(year, month, 1)).toISOString(),
      email: driverForMonth(year, month),
    });
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  for (const user of users) {
    const email = user.cognitoEmail ?? user.externalServiceUserId;
    for (const ms of user.monthlyScores) {
      const key = monthKey(ms.month);
      rows.set(key, { iso: rows.get(key)?.iso ?? ms.month, email, score: ms.score });
    }
  }
  return [...rows.values()].sort((a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime());
}

/** Collapse consecutive months with the same driver into runs (preserving display order). */
function groupDriverRuns(rows: QuarterMonthRow[]): { email?: string; span: number }[] {
  const runs: { email?: string; span: number }[] = [];
  for (const row of rows) {
    const last = runs[runs.length - 1];
    if (last && last.email === row.email) last.span += 1;
    else runs.push({ email: row.email, span: 1 });
  }
  return runs;
}

/** Semantic color for the applied bracket: rate < 1 = discount, > 1 = surcharge, 1 = neutral. */
function scoreRangeToneClass(rate: number | null | undefined): string {
  if (rate != null && rate < 1) return 'bg-green-100 text-green-700';
  if (rate != null && rate > 1) return 'bg-amber-100 text-amber-700';
  return 'bg-muted text-muted-foreground';
}

export function PolicyDrivequant() {
  const { i18n, t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();

  const policyId = policy?.id;
  const customerId = policy?.customers?.[0];

  const {
    data: drivequantId,
    status: getCustomerStatus,
    refetch: refetchDrivequantId,
  } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId!),
    enabled: !!customerId,
    select: data =>
      keyValueFieldsToObject(data.customFields ?? []).driveQuantIdConnect as string | undefined,
  });

  const { data: scores, status: listScoresStatus } = useQuery({
    ...axeceControllerListDrivequantAverageScoresQueryOptions(policyId, {
      branchId: workingBranch?.id,
    }),
    enabled: !!policyId,
    select: data => data.scores ?? [],
  });

  const { data: attachWindows } = useQuery({
    ...axeceControllerListDrivequantHistoricalUsersQueryOptions(customerId!),
    enabled: !!customerId,
    select: data => data.users,
  });

  const isLoading = getCustomerStatus === 'pending' || listScoresStatus === 'pending';
  if (isLoading) {
    return <LoadingCard />;
  }

  if (!drivequantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('drivequant.account')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{t('drivequant.notLinked')}</p>
        </CardContent>
      </Card>
    );
  }

  scores?.sort(
    (a, b) => new Date(b.period.endedAt).getTime() - new Date(a.period.endedAt).getTime(),
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('drivequant.account')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DrivequantField
            customerId={customerId}
            drivequantId={drivequantId}
            refetchDrivequantId={refetchDrivequantId}
          />
        </CardContent>
      </Card>

      {listScoresStatus === 'success' && scores.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {t('drivequant.history.title')}
              <Tooltip>
                <TooltipTrigger aria-label={t('drivequant.history.scoreInfoLabel')}>
                  <InfoIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm whitespace-pre-line">
                  {t('drivequant.history.scoreTakenIntoAccountTooltip')}
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2.5">
              {scores.map(({ score, appliedRate, appliedScoreRange, users, period }) => {
                const monthRows = buildQuarterMonthRows(period, users, attachWindows ?? []);
                const runs = groupDriverRuns(monthRows);
                const gridStyle = {
                  gridTemplateColumns: `repeat(${Math.max(monthRows.length, 1)}, minmax(0, 1fr))`,
                };
                return (
                  <div
                    key={period.startedAt}
                    className="grid grid-cols-1 items-center gap-4 rounded-lg border bg-card px-5 py-4 md:grid-cols-[150px_110px_1fr_128px]"
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('drivequant.history.repricedOn')} {formatDate(new Date(period.endedAt))}
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <span className="text-[0.62rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        {t('drivequant.history.quarterScore')}
                      </span>
                      {score != null ? (
                        <span className="text-2xl font-semibold leading-none tabular-nums">
                          {score.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t('drivequant.history.notEnoughScores')}
                        </span>
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="grid gap-2.5" style={gridStyle}>
                        {monthRows.map(month => (
                          <div key={month.iso} className="flex min-w-0 flex-col gap-1.5">
                            <div className="flex justify-between gap-1.5 text-xs">
                              <span className="text-muted-foreground">
                                {getLocaleMonth(new Date(month.iso), i18n.language)}
                              </span>
                              <span
                                className={
                                  month.score != null
                                    ? 'font-semibold tabular-nums'
                                    : 'text-muted-foreground'
                                }
                              >
                                {month.score != null ? month.score.toFixed(2) : '—'}
                              </span>
                            </div>
                            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                              {month.score != null ? (
                                <div
                                  className="h-full rounded-full bg-green-500"
                                  style={{ width: `${month.score * 10}%` }}
                                />
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 grid gap-2.5" style={gridStyle}>
                        {runs.map((run, index) => (
                          <div
                            key={`${period.startedAt}-run-${index}`}
                            className={cn(
                              'flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-[0.7rem]',
                              run.email
                                ? 'bg-muted/60 text-muted-foreground'
                                : 'border border-dashed italic text-muted-foreground',
                            )}
                            style={{ gridColumn: `span ${run.span}` }}
                          >
                            {run.email ? (
                              <>
                                <UserIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                                <span className="truncate">{run.email}</span>
                              </>
                            ) : (
                              <span className="truncate">{t('drivequant.history.noDriver')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1 justify-self-end">
                      <span className="text-[0.62rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        {t('drivequant.history.scoreRange')}
                      </span>
                      <span
                        className={cn(
                          'rounded-md px-3 py-1 text-sm font-semibold',
                          scoreRangeToneClass(appliedRate),
                        )}
                      >
                        {appliedScoreRange ?? '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DrivequantField({
  customerId,
  drivequantId,
  refetchDrivequantId,
}: {
  customerId?: string;
  drivequantId: string;
  refetchDrivequantId: () => void;
}) {
  const { t } = useTranslation();
  const [selectedDrivequantId, setSelectedDrivequantId] = useState<string>(drivequantId);
  const [showHistory, setShowHistory] = useState(false);

  const { data: possibleDrivequantIds } = useQuery({
    ...axeceControllerListPossibleDrivequantUsersQueryOptions(customerId!, {
      count: 50,
      page: 1,
    }),
    enabled: !!customerId,
  });

  const { data: historicalUsers, refetch: refetchHistoricalUsers } = useQuery({
    ...axeceControllerListDrivequantHistoricalUsersQueryOptions(customerId!),
    enabled: !!customerId,
    select: data => data.users,
  });

  const {
    mutateAsync: attachDrivequantUserIdToCustomer,
    status: attachDrivequantUserIdToCustomerStatus,
  } = useAxeceControllerAttachDrivequantUserIdToCustomer();

  const { data: selectedUser } = useQuery({
    ...axeceControllerListPossibleDrivequantUsersQueryOptions(customerId!, {
      count: 1,
      page: 1,
      externalServiceId: selectedDrivequantId,
    }),
    enabled: !!customerId && !!selectedDrivequantId,
    select: data => data.data[0],
  });

  // Option list is capped at 50 rows; the id-filtered lookup resolves a selection beyond it.
  const selectedRow =
    possibleDrivequantIds?.data.find(row => row.externalServiceId === selectedDrivequantId) ??
    selectedUser;

  const activeUser = historicalUsers?.find(user => user.detachedAt === null);

  const onCopyId = async () => {
    try {
      await navigator.clipboard.writeText(selectedDrivequantId);
      toast({ title: t('drivequant.idCopied') });
    } catch {
      // Clipboard access denied or unavailable (insecure context, permissions).
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-start justify-between w-full gap-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase text-muted-foreground whitespace-nowrap min-w-[180px]">
              {t('drivequant.accountLabel')}
            </span>
            <Select
              disabled={!customerId || attachDrivequantUserIdToCustomerStatus === 'pending'}
              value={selectedDrivequantId}
              onValueChange={async value => {
                const previousValue = selectedDrivequantId;
                setSelectedDrivequantId(value);

                try {
                  await attachDrivequantUserIdToCustomer({
                    customerId: customerId!,
                    drivequantUserId: value,
                  });
                  refetchDrivequantId();
                  refetchHistoricalUsers();
                } catch {
                  setSelectedDrivequantId(previousValue);
                }
              }}
            >
              <SelectTrigger className="border rounded px-4 py-2 min-w-[280px]">
                <SelectValue placeholder={t('drivequant.accountLabel')}>
                  {selectedRow?.email ?? selectedDrivequantId}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {possibleDrivequantIds?.data
                  .filter(row => row.externalServiceId !== '')
                  .map(row => (
                    <SelectItem key={row.externalServiceId} value={row.externalServiceId}>
                      {row.email} - {row.externalServiceId}{' '}
                      {row.enabled ? t('drivequant.users.active') : t('drivequant.users.inactive')}
                    </SelectItem>
                  ))}
                {possibleDrivequantIds?.data
                  .filter(row => row.externalServiceId === '')
                  .map(row => (
                    <SelectItem key={row.userId} value={row.userId} disabled>
                      {row.email} — {t('drivequant.users.noAccount')}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {selectedRow &&
              (selectedRow.enabled ? (
                <Badge className="gap-1 border-transparent bg-green-100 text-green-700 hover:bg-green-100">
                  <CheckCircle2Icon className="h-3 w-3" aria-hidden="true" />
                  {t('drivequant.users.active')}
                </Badge>
              ) : (
                <Badge className="border-transparent bg-amber-100 text-amber-700 hover:bg-amber-100">
                  {t('drivequant.users.inactive')}
                </Badge>
              ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase text-muted-foreground whitespace-nowrap min-w-[180px]">
              {t('drivequant.idLabel')}
            </span>
            <code className="rounded bg-muted px-2 py-1 text-xs font-mono">
              {selectedDrivequantId}
            </code>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('drivequant.copyId')}
              onClick={onCopyId}
            >
              <CopyIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

          {activeUser ? (
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold uppercase text-muted-foreground whitespace-nowrap min-w-[180px]">
                {t('drivequant.attachedAt')}
              </span>
              <span className="text-sm">{formatDate(new Date(activeUser.attachedAt))}</span>
            </div>
          ) : null}
        </div>

        <Button
          size="default"
          variant="link"
          onClick={() =>
            window.open(
              `https://lea.drivequant.com/#/app/dashboard/${selectedDrivequantId}`,
              '_blank',
            )
          }
        >
          {t('drivequant.openAccount')}
        </Button>
      </div>

      {historicalUsers && historicalUsers.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="self-start gap-1 px-0 text-muted-foreground hover:bg-transparent"
            onClick={() => setShowHistory(show => !show)}
          >
            {showHistory ? (
              <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            )}
            {t('drivequant.historicalUsers.toggle', { count: historicalUsers.length })}
          </Button>
          {showHistory ? (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('drivequant.historicalUsers.user')}</TableHead>
                    <TableHead>{t('drivequant.historicalUsers.attached')}</TableHead>
                    <TableHead>{t('drivequant.historicalUsers.until')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historicalUsers.map(user => (
                    <TableRow key={`${user.externalServiceUserId}-${user.attachedAt}`}>
                      <TableCell>{user.cognitoEmail ?? user.externalServiceUserId}</TableCell>
                      <TableCell>{formatDate(new Date(user.attachedAt))}</TableCell>
                      <TableCell>
                        {user.detachedAt ? (
                          formatDate(new Date(user.detachedAt))
                        ) : (
                          <Badge className="gap-1 border-transparent bg-green-100 text-green-700 hover:bg-green-100">
                            <CheckCircle2Icon className="h-3 w-3" aria-hidden="true" />
                            {t('drivequant.historicalUsers.active')}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
